import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { NABadge } from '../NABadge';
import { FormulaTooltip } from '../FormulaTooltip';
import AIInsightPanel from '../AIInsightPanel';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Target,
  Percent,
  Coins,
  PiggyBank,
  Activity,
  RefreshCw,
  ChevronRight,
  Building2,
  MapPin,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Zap,
  Search,
  Download,
  Bell,
  FileSpreadsheet,
  ZoomIn,
  Clock,
  GraduationCap
} from 'lucide-react';
import { Badge } from '../ui/badge';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import { ExportButton, jsonToCSV, downloadCSV } from '../ExportUtils';
import { AskAIButton, AskAIOverlay } from '../AskAIButton';
import { WalkthroughButton } from '../InteractiveWalkthrough';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// KPI Card Component with financial-grade styling
const KPICard = ({ label, value, unit, delta, deltaDirection, benchmark, icon: Icon, onClick, color = "primary", brandId, onAskAI }) => {
  const isPositive = deltaDirection === "up";
  const colorClasses = {
    primary: "from-violet-500/20 to-violet-600/10 border-violet-500/30",
    secondary: "from-pink-500/20 to-pink-600/10 border-pink-500/30",
    success: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30",
    warning: "from-amber-500/20 to-amber-600/10 border-amber-500/30",
    info: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30"
  };

  // Format large numbers with 2 decimal places
  const formatValue = (val) => {
    if (val === null || val === undefined) return '0';
    if (typeof val !== 'number') return val;
    if (val >= 10000000) return `${(val / 10000000).toFixed(2)}Cr`;
    if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
    if (val >= 1000) return `${(val / 1000).toFixed(2)}K`;
    return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const contextString = `${label}: ${formatValue(value)}${unit || ''}${delta ? `, ${deltaDirection === 'up' ? '+' : '-'}${delta}% change` : ''}${benchmark ? `, Target: ${benchmark}` : ''}`;

  return (
    <Card 
      className={`bg-gradient-to-br ${colorClasses[color]} border cursor-pointer hover:shadow-lg transition-all duration-300 group relative`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="p-2.5 rounded-xl bg-background/50 backdrop-blur">
            <Icon className="w-5 h-5 text-foreground" />
          </div>
          <div className="flex items-center gap-1">
            {delta !== undefined && (
              <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
                isPositive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(delta)}%
              </div>
            )}
            <AskAIButton
              context={contextString}
              chartTitle={label}
              chartData={[{ name: label, value: value, delta: delta, benchmark: benchmark }]}
              brandId={brandId}
              dashboardType="command_center"
              size="icon"
              variant="ghost"
              className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium truncate">{label}</p>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold tracking-tight truncate">
              {formatValue(value)}
            </span>
            {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
          </div>
          {benchmark && (
            <p className="text-xs text-muted-foreground truncate">Target: {benchmark}</p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 absolute bottom-4 right-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </CardContent>
    </Card>
  );
};

// Comparison Card for metrics vs Industry Benchmarks
const ComparisonCard = ({ title, current, benchmark, vsBenchmark, unit = "₹", benchmarkLabel = "Industry Avg" }) => (
  <Card className="bg-card/50">
    <CardContent className="p-5">
      <h4 className="text-sm font-medium text-muted-foreground mb-4">{title}</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
          <p className="text-xs text-muted-foreground mb-1">Your Performance</p>
          <p className="text-xl font-bold text-violet-400">{unit}{current?.toLocaleString()}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground mb-1">{benchmarkLabel}</p>
          <p className="text-xl font-bold">{unit}{benchmark?.toLocaleString()}</p>
        </div>
      </div>
      <div className={`mt-3 flex items-center justify-center gap-2 p-2 rounded-lg ${
        vsBenchmark >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
      }`}>
        {vsBenchmark >= 0 ? (
          <TrendingUp className="w-4 h-4 text-emerald-400" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-400" />
        )}
        <span className={`text-sm font-semibold ${vsBenchmark >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {vsBenchmark >= 0 ? '+' : ''}{vsBenchmark}% vs Benchmark
        </span>
      </div>
    </CardContent>
  </Card>
);

export default function CommandCenter() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ city: 'all', store: 'all', dateRange: 'lifetime' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });
  const [industryBenchmark, setIndustryBenchmark] = useState(null);

  // Open chart zoom modal
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({
      open: true,
      type: chartType,
      data: chartData,
      title: chartTitle,
      config: chartConfig
    });
  };

  const closeChartZoom = () => {
    setChartZoom({ open: false, type: '', data: [], title: '', config: {} });
  };

  // Export dashboard data to CSV
  const handleExport = () => {
    if (!data) return;
    
    // Helper to format CSV values properly
    const formatCsvValue = (val) => {
      if (val === null || val === undefined) return '';
      const strVal = String(val);
      if (strVal.includes(',') || strVal.includes('"')) {
        return `"${strVal.replace(/"/g, '""')}"`;
      }
      return strVal;
    };
    
    const kpis = data.kpis || {};
    const csvRows = [
      ['Metric', 'Value', 'Unit', 'Benchmark'],
      ['Loyalty Revenue Contribution', (kpis.loyalty_contribution?.value || 0).toFixed(1), '%', kpis.loyalty_contribution?.benchmark || ''],
      ['Active Members', kpis.active_members?.value || 0, '', kpis.active_members?.benchmark || ''],
      ['Repeat Ratio', (kpis.repeat_ratio?.value || 0).toFixed(1), '%', kpis.repeat_ratio?.benchmark || ''],
      ['Earn-Burn Ratio', (kpis.earn_burn_ratio?.value || 0).toFixed(1), '%', kpis.earn_burn_ratio?.benchmark || ''],
      ['Net Loyalty Margin', (kpis.net_margin?.value || 0).toFixed(1), '%', kpis.net_margin?.benchmark || ''],
      ['Sales', Math.round(data.totals?.total_revenue || 0), '₹', ''],
      ['Sales Return', Math.round(data.totals?.return_amount || 0), '₹', ''],
      ['Net Value', Math.round(data.totals?.gross_sales || 0), '₹', ''],
      ['Total Customers', data.totals?.total_customers || 0, '', ''],
    ];
    
    const csvContent = csvRows.map(row => row.map(formatCsvValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `command_center_${selectedBrand?.name || 'data'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  // Request counter to ensure unique requests
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Generate unique request ID to prevent caching
    requestIdRef.current += 1;
    const requestId = `${Date.now()}-${requestIdRef.current}`;
    
    // Check for live Data Bridge - if brand has db_brand_id, mark as live
    const dbBrandId = getDbBrandId();
    
    // Always use original API for proper data structure
    try {
      // Fetch dashboard data and industry benchmark in parallel
      // Add cache-busting params to ensure fresh data
      const [dashboardResponse, benchmarkResponse] = await Promise.all([
        axios.post(
          `${API}/api/analytics/command-center?_t=${Date.now()}&_rid=${requestId}`,
          { brand_id: selectedBrand.brand_id, filters: { ...filters, ...(dateFrom && dateTo ? { date_from: dateFrom, date_to: dateTo } : {}) } },
          { 
            withCredentials: true,
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'X-Request-ID': requestId
            }
          }
        ),
        axios.get(
          `${API}/api/industry/benchmarks/${selectedBrand.brand_id}?_t=${Date.now()}`,
          { 
            withCredentials: true,
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }
        ).catch(() => null)
      ]);
      
      // Force state update by creating new object reference
      setData({...dashboardResponse.data.data});
      // Set live status from response or from brand settings
      setIsLiveData(dashboardResponse.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Set industry benchmark if available
      if (benchmarkResponse?.data) {
        setIndustryBenchmark({...benchmarkResponse.data});
      }
    } catch (err) {
      console.error('Command Center data error:', err);
      setError('Failed to load Command Center data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, filters, dateFrom, dateTo, getDbBrandId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrillDown = (type, value, title) => {
    setDrillDown({ open: true, type, data: { value }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  // Format large numbers with 2 decimal places
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0';
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    return `₹${value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  // Format as Cr only (no L)
  const formatCr = (value) => {
    if (value === null || value === undefined) return '0';
    if (typeof value !== 'number') value = parseFloat(value) || 0;
    if (value >= 10000000) return `${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 1000) return `${(value / 10000000).toFixed(4)}Cr`;
    return value.toFixed(2);
  };

  const formatPoints = (value) => {
    if (value === null || value === undefined) return '0';
    if (value >= 10000000) return `${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(2)}L`;
    return value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    if (value >= 10000000) return `${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
    return value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="command-center-loading">
        <div className="flex flex-col items-center gap-4">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Loading Command Center...</p>
        </div>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="command-center-no-brand">
        <div className="flex flex-col items-center gap-4 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground" />
          <div>
            <p className="text-lg font-medium mb-2">No Brand Selected</p>
            <p className="text-muted-foreground">Please select a brand from the dropdown to view analytics</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const comparison = data?.comparison || {};
  const trends = data?.trends || {};
  const pointsEconomy = data?.points_economy || {};
  const filterOptions = data?.filterOptions || data?.filters || {};
  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="command-center">
      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500">
              <Activity className="w-6 h-6 text-white" />
            </div>
            Loyalty Command Center
            {isLiveData && (
              <Badge className="ml-2 bg-emerald-500 text-white animate-pulse">
                <Zap className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            )}
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground mt-1">
            {isLiveData 
              ? `Live data: ${data?.kpis?.total_customers?.toLocaleString() || 0} customers`
              : 'Executive-level loyalty program performance'
            }
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Training Button */}
          <WalkthroughButton dashboardId="command-center" className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10" />
          {/* Date Range Filter */}
          <Select value={filters.dateRange} onValueChange={(v) => { setDateFrom(''); setDateTo(''); setFilters(prev => ({ ...prev, dateRange: v })); }}>
            <SelectTrigger className={`w-32 ${loading ? 'animate-pulse' : ''}`} data-testid="filter-date">
              <Calendar className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lifetime">Lifetime</SelectItem>
              <SelectItem value="ytd">YTD</SelectItem>
              <SelectItem value="12m">Last 12M</SelectItem>
              <SelectItem value="6m">Last 6M</SelectItem>
              <SelectItem value="3m">Last 3M</SelectItem>
              {dateFrom && dateTo && <SelectItem value="custom">Custom</SelectItem>}
            </SelectContent>
          </Select>

          {/* City Filter */}
          <Select value={filters.city} onValueChange={(v) => setFilters(prev => ({ ...prev, city: v }))}>
            <SelectTrigger className="w-40" data-testid="filter-city">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {filterOptions.cities?.map((city, idx) => (
                <SelectItem key={`city-${idx}`} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Store Filter - Searchable */}
          <SearchableSelect
            value={filters.store}
            onValueChange={(v) => setFilters(prev => ({ ...prev, store: v }))}
            options={filterOptions.stores || []}
            placeholder="All Stores"
            searchPlaceholder="Search stores..."
            emptyText="No stores found"
            icon={Building2}
            triggerClassName="w-48"
            data-testid="filter-store"
          />

          {/* Custom Date Range Filter */}
          <div className="flex items-center gap-1 ml-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                if (e.target.value && dateTo) {
                  setFilters(prev => ({ ...prev, dateRange: 'custom' }));
                }
              }}
              className="h-9 px-2 rounded-md border border-border bg-background text-sm text-foreground w-36"
              data-testid="filter-date-from"
              placeholder="From"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                if (dateFrom && e.target.value) {
                  setFilters(prev => ({ ...prev, dateRange: 'custom' }));
                }
              }}
              className="h-9 px-2 rounded-md border border-border bg-background text-sm text-foreground w-36"
              data-testid="filter-date-to"
              placeholder="To"
            />
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 text-xs"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setFilters(prev => ({ ...prev, dateRange: 'lifetime' }));
                }}
                data-testid="filter-date-clear"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setSearchOpen(!searchOpen)}
              className="relative"
              data-testid="search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={fetchData}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleExport}
              data-testid="download-btn"
              title="Export Summary"
            >
              <Download className="w-4 h-4" />
            </Button>
            
            {/* Full Report Export */}
            <ExportButton 
              brandId={selectedBrand?.brand_id}
              dashboardType="command_center"
              label="Full Report"
              className="h-9 text-sm"
            />
            
            <Button 
              variant="outline" 
              size="icon"
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
            </Button>
          </div>
        </div>
        
        {/* Search Bar (conditional) */}
        {searchOpen && (
          <div className="mt-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search stores, cities, metrics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* KPI Cards Row - Updated to show Total Revenue, Total Members, Total Bills, Total Stores with breakdowns */}
      <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
        {/* Sales with Returns and Net Value breakdown */}
        <Card className="bg-gradient-to-br from-violet-500/20 to-violet-600/10 border-violet-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'total_revenue', 'Sales')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="w-4 h-4 text-violet-400" />
              <FormulaTooltip metric="total_revenue"><span className="text-xs text-muted-foreground">Sales</span></FormulaTooltip>
            </div>
            <div className="text-2xl font-bold">{loading ? '...' : formatCurrency(totals?.total_revenue)}</div>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between text-red-400">
                <span>Sales Return</span>
                <span>{formatCurrency(totals?.return_amount)}</span>
              </div>
              <div className="flex justify-between text-emerald-400">
                <span>Net Value</span>
                <span>{formatCurrency(totals?.gross_sales)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Members — registered count + active/non-active + repeat/one-time breakdown */}
        <Card className="bg-gradient-to-br from-pink-500/20 to-pink-600/10 border-pink-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'total_members', 'Total Members')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-pink-400" />
              <FormulaTooltip metric="total_customers"><span className="text-xs text-muted-foreground">Total Members</span></FormulaTooltip>
              {totals?.sync_in_progress && (
                <span
                  data-testid="members-sync-warning"
                  title={`Transaction sync still in progress for this brand — Active count is from local cache only and may underreport. ${totals?.sync_progress_note || ''}`}
                  className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 cursor-help"
                >
                  SYNC
                </span>
              )}
            </div>
            <div className="text-2xl font-bold" data-testid="kpi-total-members">{loading ? '...' : formatNumber(totals?.total_customers)}</div>
            <div className="mt-2 grid grid-cols-1 gap-1 text-xs">
              <div className="flex justify-between text-emerald-400">
                <FormulaTooltip metric="active_customers"><span>Active</span></FormulaTooltip>
                <span data-testid="kpi-active-customers">
                  {formatNumber(totals?.active_customers)}
                  {totals?.total_customers > 0 && ` (${((totals?.active_customers / totals?.total_customers) * 100).toFixed(2)}%)`}
                </span>
              </div>
              <div className="flex justify-between text-rose-400">
                <FormulaTooltip metric="inactive_customers"><span>Non-Active</span></FormulaTooltip>
                <span data-testid="kpi-inactive-customers">
                  {formatNumber(totals?.inactive_customers)}
                  {totals?.total_customers > 0 && ` (${((totals?.inactive_customers / totals?.total_customers) * 100).toFixed(2)}%)`}
                </span>
              </div>
              <div className="flex justify-between text-cyan-400 pt-1 border-t border-border/30">
                <FormulaTooltip metric="repeat_customers"><span>Repeat</span></FormulaTooltip>
                <span>{formatNumber(totals?.repeat_customers)} ({totals?.repeat_cust_pct}%)</span>
              </div>
              <div className="flex justify-between text-amber-400">
                <FormulaTooltip metric="one_timers"><span>One-Time</span></FormulaTooltip>
                <span>{formatNumber(totals?.one_time_customers)} ({totals?.one_time_cust_pct}%)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bills Breakdown - Sales + Returns with exact counts */}
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'total_bills', 'Total Bills')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
              <FormulaTooltip metric="total_transactions"><span className="text-xs text-muted-foreground">Bills</span></FormulaTooltip>
            </div>
            <div className="text-2xl font-bold">{loading ? '...' : (totals?.total_bills || 0).toLocaleString('en-IN')}</div>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between text-emerald-400">
                <span>Sales Bills</span>
                <span>{(totals?.sales_bills || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-red-400">
                <span>Return Bills</span>
                <span>{(totals?.return_bills || 0).toLocaleString('en-IN')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Stores */}
        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'total_stores', 'Total Stores')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-amber-400" />
              <FormulaTooltip metric="total_stores"><span className="text-xs text-muted-foreground">Total Stores</span></FormulaTooltip>
            </div>
            <div className="text-2xl font-bold">{formatNumber(totals?.total_stores)}</div>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between text-muted-foreground">
                <FormulaTooltip metric="avg_atv"><span>ATV</span></FormulaTooltip>
                <span>{formatCurrency(totals?.overall_atv)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <FormulaTooltip metric="visit_frequency"><span>Visit Freq</span></FormulaTooltip>
                <span>{totals?.visit_frequency?.toFixed(2)}x</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Repeat Ratio with actual numbers */}
        <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'repeat_ratio', 'Repeat Ratio')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-cyan-400" />
              <FormulaTooltip metric="repeat_ratio"><span className="text-xs text-muted-foreground">Repeat Ratio</span></FormulaTooltip>
            </div>
            <div className="text-2xl font-bold">{kpis.repeat_ratio?.value}%</div>
            <div className="mt-2 text-xs text-muted-foreground">
              <div>{formatNumber(totals?.repeat_customers)} repeat / {formatNumber(totals?.total_customers)} total</div>
            </div>
            <div className="mt-2 pt-2 border-t border-border/50">
              <div className="flex justify-between items-center text-xs">
                <FormulaTooltip metric="excl_same_day_repeat"><span className="text-muted-foreground">Excl. Same Day</span></FormulaTooltip>
                <span className="font-semibold text-cyan-400">{totals?.repeat_excl_same_day_pct || 0}%</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {(totals?.repeat_excl_same_day || 0).toLocaleString('en-IN')} repeat / {formatNumber(totals?.total_customers)} total
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earn-Burn Ratio with actual numbers */}
        <Card className="bg-gradient-to-br from-violet-500/20 to-violet-600/10 border-violet-500/30 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openDrillDown('kpi', 'earn_burn_ratio', 'Earn-Burn Ratio')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank className="w-4 h-4 text-violet-400" />
              <FormulaTooltip metric="earn_burn_ratio"><span className="text-xs text-muted-foreground">Earn-Burn Ratio</span></FormulaTooltip>
            </div>
            <div className="text-2xl font-bold">
              {(kpis.earn_burn_ratio?.value === null || kpis.earn_burn_ratio?.value === undefined || kpis.earn_burn_ratio?.value === 0) ? (
                <NABadge reason={kpis.earn_burn_ratio?.na_reason || 'Points earn/redeem data not ingested for this brand'} dataTestId="cmd-earn-burn-na" />
              ) : (
                `${kpis.earn_burn_ratio.value}%`
              )}
            </div>
            <div className="mt-2 text-xs text-muted-foreground truncate">
              {kpis.earn_burn_ratio?.value ? <div>{kpis.earn_burn_ratio?.actual_numbers?.split('/')[0] || 'Redeemed'}</div> : <div>0 redeemed</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real GPT-5.2 AI Insight (cached 24h) */}
      <AIInsightPanel
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name || selectedBrand?.brand_name}
        kpiName="command_center_summary"
        context={{
          total_customers: totals?.total_customers,
          total_revenue: totals?.total_revenue,
          total_bills: totals?.total_bills,
          total_stores: totals?.total_stores,
          repeat_customers: totals?.repeat_customers,
          one_time_customers: totals?.one_time_customers,
          repeat_cust_pct: totals?.repeat_cust_pct,
          one_time_cust_pct: totals?.one_time_cust_pct,
          repeat_excl_same_day_pct: totals?.repeat_excl_same_day_pct,
          overall_atv: totals?.overall_atv,
          visit_frequency: totals?.visit_frequency,
          earn_burn_ratio: data?.kpis?.earn_burn_ratio?.value,
          active_rate_90d: data?.loyalty_metrics?.active_rate_90d?.value,
        }}
      />

      {/* Points Economy Section - ACTUAL DATA */}
      {data?.points_economy && (() => {
        const pe = data.points_economy;
        const hasPointsData = (pe.total_earned || 0) > 0 || (pe.total_redeemed || 0) > 0;
        if (!hasPointsData) {
          return (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-4 flex items-start gap-3">
                <Activity className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-200">Points Economy — N/A</h3>
                  <p className="text-sm text-amber-100/80 mt-1">
                    No points earn/redeem data ingested for this brand. Points Economy module requires loyalty point ledger entries from the POS.
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        }
        return (
        <Card className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border-violet-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-violet-400" />
                Points Economy
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                Actual Data
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Points Earned */}
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xs text-emerald-400 mb-1">Total Earned</p>
                <div className="text-lg font-bold text-emerald-400">
                  {formatCr(data.points_economy?.total_earned)} pts
                </div>
                <p className="text-[10px] text-muted-foreground">₹{formatCr(data.points_economy?.total_earned_inr)}</p>
              </div>
              
              {/* Points Redeemed (Burn) */}
              <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-xs text-cyan-400 mb-1">Redeemed (Burn)</p>
                <div className="text-lg font-bold text-cyan-400">
                  {formatCr(data.points_economy?.total_redeemed)} pts
                </div>
                <p className="text-[10px] text-muted-foreground">₹{formatCr(data.points_economy?.total_redeemed_inr)} | {data.points_economy?.redeem_bills?.toLocaleString()} bills</p>
              </div>
              
              {/* Reversed/Returned */}
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-xs text-red-400 mb-1">Reversed (Returns)</p>
                <div className="text-lg font-bold text-red-400">
                  {formatCr(Math.abs(data.points_economy?.reversed_points || 0))} pts
                </div>
                <p className="text-[10px] text-muted-foreground">{data.points_economy?.reverse_bills?.toLocaleString() || 0} bills</p>
              </div>
              
              {/* Outstanding Liability */}
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400 mb-1">Outstanding Liability</p>
                <div className="text-lg font-bold text-amber-400">
                  {formatCr(data.points_economy?.liability)} pts
                </div>
                <p className="text-[10px] text-muted-foreground">₹{formatCr(data.points_economy?.liability_value)}</p>
              </div>
              
              {/* Redemption Rate */}
              <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
                <p className="text-xs text-violet-400 mb-1">Burn Rate</p>
                <div className="text-lg font-bold text-violet-400">
                  {data.points_economy?.earn_burn_ratio?.toFixed(1)}%
                </div>
                <p className="text-[10px] text-muted-foreground">of earned points redeemed</p>
              </div>
            </div>
            
            {/* Liability Calculation Formula */}
            <div className="mt-3 p-2 rounded bg-background/30 text-xs text-muted-foreground">
              <span className="text-amber-400">Liability</span> = <span className="text-emerald-400">Earned</span> - <span className="text-cyan-400">Redeemed</span> - <span className="text-red-400">Reversed</span> = {formatCr(data.points_economy?.total_earned)} - {formatCr(data.points_economy?.total_redeemed)} - {formatCr(Math.abs(data.points_economy?.reversed_points || 0))} = <span className="text-amber-400 font-bold">{formatCr(data.points_economy?.liability)} pts</span>
              {data.points_economy?.point_value && (
                <span className="ml-2 text-cyan-400">(1 pt = ₹{data.points_economy.point_value.toFixed(2)})</span>
              )}
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Loyalty Metrics Section */}
      {data?.loyalty_metrics && (
        <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/10 border-slate-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-400" />
              Customer Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* ATV */}
              <div className="p-3 rounded-lg bg-background/50 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Avg Transaction Value</p>
                <div className="text-lg font-bold">{formatCurrency(totals?.overall_atv)}</div>
              </div>
              
              {/* Customer LTV */}
              <div className="p-3 rounded-lg bg-background/50 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Customer LTV</p>
                <div className="text-lg font-bold">{formatCurrency(data.loyalty_metrics.clv?.value)}</div>
              </div>
              
              {/* Visit Frequency */}
              <div className="p-3 rounded-lg bg-background/50 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Visit Frequency</p>
                <div className="text-lg font-bold">{data.loyalty_metrics.visit_frequency?.value?.toFixed(2)}x</div>
              </div>
              
              {/* Active Rate */}
              <div className="p-3 rounded-lg bg-background/50 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Active Rate (90d)</p>
                <div className="text-lg font-bold">
                  {(data.loyalty_metrics.active_rate_90d?.value === null || data.loyalty_metrics.active_rate_90d?.value === undefined || data.loyalty_metrics.active_rate_90d?.value === 0) ? (
                    <NABadge reason={data.loyalty_metrics.active_rate_90d?.na_reason || "Customer last_transaction_date not available — cannot compute 90-day active count"} dataTestId="cmd-active-rate-na" />
                  ) : (
                    `${data.loyalty_metrics.active_rate_90d.value}%`
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Monthly Member Count Trend */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Monthly Member Count
              </CardTitle>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-violet-500" />
                  <span>New Members</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span>Active Members</span>
                </div>
                <AskAIButton
                  context="Monthly member count trend showing new members acquired and active members over time"
                  chartTitle="Monthly Member Count"
                  chartData={trends.revenue || []}
                  brandId={selectedBrand?.brand_id}
                  dashboardType="command_center"
                />
                <ChartZoomButton 
                  onClick={() => openChartZoom('composed', trends.revenue || [], 'Monthly Member Count', { xKey: 'month' })}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={trends.revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  tickFormatter={(v) => formatNumber(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip 
                  formatter={(value, name) => [formatNumber(value), name === 'customers' ? 'New Members' : 'Active Members']}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar
                  dataKey="customers"
                  fill="rgba(139, 92, 246, 0.7)"
                  radius={[4, 4, 0, 0]}
                  name="customers"
                />
                <Line
                  type="monotone"
                  dataKey="transactions"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  name="transactions"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance Metrics - ATV and Frequency from actual data */}
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border-indigo-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Avg Transaction Value</p>
              <div className="text-xl font-bold">{formatCurrency(comparison.atv?.current || totals?.overall_atv)}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border-teal-500/30">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Avg Purchase Frequency</p>
              <div className="text-xl font-bold">{(comparison.frequency?.current || totals?.visit_frequency || 0).toFixed(2)}x</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Points Economy Section */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Points Liability Trend */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-500" />
                Points Economy Trend
              </CardTitle>
              <div className="flex items-center gap-2">
                <AskAIButton
                  context="Points economy showing earned vs redeemed points and total liability over time"
                  chartTitle="Points Economy Trend"
                  chartData={trends.points || []}
                  brandId={selectedBrand?.brand_id}
                  dashboardType="command_center"
                />
                <ChartZoomButton 
                  onClick={() => openChartZoom('composed', trends.points || [], 'Points Economy Trend', { xKey: 'month' })}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={trends.points || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => v?.slice(5)}
                />
                <YAxis 
                  yAxisId="left"
                  tickFormatter={formatPoints}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={formatPoints}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip 
                  formatter={(value, name) => [formatPoints(value), name]}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="earned" fill="#10B981" name="Earned" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="redeemed" fill="#EC4899" name="Redeemed" radius={[4, 4, 0, 0]} />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="liability" 
                  stroke="#F59E0B" 
                  strokeWidth={3}
                  dot={{ fill: '#F59E0B' }}
                  name="Liability"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Points Summary Card */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PiggyBank className="w-4 h-4 text-amber-500" />
              Points Liability Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-xl bg-background/50">
              <p className="text-xs text-muted-foreground mb-1">Outstanding Liability</p>
              <p className="text-3xl font-bold text-amber-500">
                ₹{((pointsEconomy.liability_value || 0) / 100000).toFixed(2)}L
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {formatNumber(pointsEconomy.liability || pointsEconomy.outstanding_liability || 0)} points
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <p className="text-xs text-muted-foreground">Total Earned</p>
                <p className="text-lg font-semibold text-emerald-400">{formatPoints(pointsEconomy.total_earned)}</p>
              </div>
              <div className="p-3 rounded-lg bg-pink-500/10">
                <p className="text-xs text-muted-foreground">Total Redeemed</p>
                <p className="text-lg font-semibold text-pink-400">{formatPoints(pointsEconomy.total_redeemed)}</p>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-red-500/10">
              <p className="text-xs text-muted-foreground">Expired Points</p>
              <p className="text-lg font-semibold text-red-400">{formatPoints(pointsEconomy.expired_points || pointsEconomy.total_expired || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deep AI Insights - FundleBrain Chief Intelligence Engine */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="command_center"
        dashboardData={data}
        kpis={{
          total_customers: { value: kpis.active_members?.value || 0 },
          total_revenue: { value: data?.totals?.total_revenue || 0 },
          repeat_rate: { value: kpis.repeat_ratio?.value || 0 },
          avg_atv: { value: comparison.atv?.loyalty || 0 },
          loyalty_contribution: { value: kpis.loyalty_contribution?.value || 0 },
          earn_burn_ratio: { value: kpis.earn_burn_ratio?.value || 0 }
        }}
        trends={trends}
        points={{
          total_earned: pointsEconomy.total_earned || 0,
          total_redeemed: pointsEconomy.total_redeemed || 0,
          liability: pointsEconomy.liability || pointsEconomy.outstanding_liability || 0,
          liability_value: pointsEconomy.liability_value || 0
        }}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="command_center"
      />
    </div>
  );
}
